import { IDictionary } from 'util/domain';
import { 
	MenuItemConfig,
	IMenuItem
} from 'sharebase/domain';
import { MenuItemFactory } from 'sharebase/factories';

export enum MenuPriority {
	HIGH, 
	MEDIUMHIGH,
	MEDIUM,
	MEDIUMLOW,
	LOW
};

export class Menu {
	public static $inject = [
		'$q',
		'sbMenuItemFactory'
	];

	private _items:IDictionary<IMenuItem> = {};
	private _futurePriorities:IDictionary<MenuPriority> = {};

	public get items() {
		return this._items;
	}

	constructor(
		protected readonly $q:ng.IQService,
		protected readonly sbMenuItemFactory:MenuItemFactory
	){
	}

	public addItem(item:IMenuItem, priority?:MenuPriority){
		this._items[item.action] = item;
		this.markItem(item ,priority);

		return () => {
			this.removeItems(item.action);
			item = null;
		};
	}

	public getItem(name:string){
		if(name in this._items) {
			return this._items[name];
		}
	}

	/**
	 * Retrieves menu items matching the specified names.
	 * @param actionNames A vardiatic list of menu items to retrieve by action name.
	 * Specifying no name will return all items.
	 */
	public getItems(...actionNames:string[]){
		if(actionNames.length < 1){
			actionNames = Object.keys(this._items);
		}
		return actionNames
			.filter(name => name in this._items)
			.map(name => this._items[name]);
	}

	public removeItems(...actions:string[]){
		actions.forEach(action => {
			if(action in this._items){
				this._items[action].removeAllValidators();
				this._items[action] = null;
				delete this._items[action];
			}
		});
	}

	public add(config:MenuItemConfig, priority?:MenuPriority) {
		let {action, label, icon, trigger, validator} = config;
		let item = this.sbMenuItemFactory.create(action, label, icon);

		if(trigger){
			item.addTrigger(trigger);
		}

		if(validator){
			item.addValidator(validator);
		}		

		return this.addItem(item, priority);
	}

	public copyFromMenu(actionName:string, from:Menu, removeValidators:boolean = false) {
		let item = from.getItem(actionName);
		if(!item){
			throw new Error(`No menu item to copy for: ${actionName}`);
		} 

		let remover = this.addItem(item.clone());
		if(removeValidators) {
			item.removeAllValidators();
		}

		return remover;
	}

	
    public setPriority(priority:MenuPriority, ...itemNames:string[]){
        itemNames.forEach(name => {
            let item = this.getItem(name);
            // if an item already exists, explicitly set
            if(item){
                item.priority = priority;
            }
            // set the priority for a future action type addition
            else {
                this._futurePriorities[name] = priority;
            }
        });

        return this;
    }

    private markItem(item:IMenuItem, priority?:MenuPriority){
        // prefer explicitly set priority
        if(angular.isDefined(priority)){            
            item.priority = priority
        }
        // assign future priority (if any)
        else if(item.action in this._futurePriorities) {            
			item.priority = this._futurePriorities[item.action];
			this._futurePriorities[item.action] = null;
			delete this._futurePriorities[item.action];
		}
		// if an existing priority does not exist, use default
		else if(!angular.isDefined(item.priority)){
			item.priority = MenuPriority.MEDIUM;
		}
    }

	public clear(){
		this._items = {};
	}

	public getActiveItems(target?:any){	
		let items = Object.keys(this._items).map(k => this._items[k]);
		let visibilityPromises = items.map(i => i.canShow(target));
		return this.$q.all(visibilityPromises)
			.then(() => items.filter(i => i.visible));
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/domain/menu.ts